import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface MissingReferences {
  characters: string[];  // 缺失的角色/主体
  props: string[];       // 缺失的道具
  scenes: string[];      // 缺失的场景
  voiceRoles: string[];  // 缺失的配音角色
}

interface MissingReferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  missingRefs: MissingReferences;
}

/**
 * 缺失引用警告弹窗
 * 当分镜脚本中引用的角色/道具/场景在当前列表中不存在时显示
 */
export default function MissingReferencesModal({
  isOpen,
  onClose,
  onConfirm,
  missingRefs,
}: MissingReferencesModalProps) {
  const hasCharacters = missingRefs.characters.length > 0;
  const hasProps = missingRefs.props.length > 0;
  const hasScenes = missingRefs.scenes.length > 0;
  const hasVoiceRoles = missingRefs.voiceRoles.length > 0;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md bg-bg-page rounded-2xl border border-border-subtle shadow-2xl">
          {/* 头部 */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
              </div>
              <DialogTitle className="text-base font-semibold text-gray-50">
                检测到缺失的引用
              </DialogTitle>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-bg-elevated rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* 内容 */}
          <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-gray-300">
              分镜脚本中引用了以下已被删除或不存在的元素，继续生成可能导致画面效果不佳：
            </p>

            <div className="space-y-3">
              {hasCharacters && (
                <div className="p-3 bg-bg-elevated rounded-lg border border-border-subtle">
                  <div className="text-xs font-medium text-amber-500 mb-2">缺失的主体/角色</div>
                  <div className="flex flex-wrap gap-1.5">
                    {missingRefs.characters.map((name, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hasVoiceRoles && (
                <div className="p-3 bg-bg-elevated rounded-lg border border-border-subtle">
                  <div className="text-xs font-medium text-amber-500 mb-2">缺失的配音角色</div>
                  <div className="flex flex-wrap gap-1.5">
                    {missingRefs.voiceRoles.map((name, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hasProps && (
                <div className="p-3 bg-bg-elevated rounded-lg border border-border-subtle">
                  <div className="text-xs font-medium text-amber-500 mb-2">缺失的道具</div>
                  <div className="flex flex-wrap gap-1.5">
                    {missingRefs.props.map((name, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hasScenes && (
                <div className="p-3 bg-bg-elevated rounded-lg border border-border-subtle">
                  <div className="text-xs font-medium text-amber-500 mb-2">缺失的场景</div>
                  <div className="flex flex-wrap gap-1.5">
                    {missingRefs.scenes.map((name, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400">
              建议：返回修改分镜脚本，或补充缺失的角色/道具/场景后再生成。
            </p>
          </div>

          {/* 底部按钮 */}
          <div className="flex gap-3 p-4 border-t border-border-subtle">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-bg-elevated hover:bg-bg-surface rounded-lg transition-colors"
            >
              返回修改
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
            >
              仍然继续
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
